<template>
  <div>
    <b-button
      variant="primary"
      class="d-inline-flex align-items-center"
      @click="open"
    >
      <feather-icon
        icon="UploadCloudIcon"
        size="16"
        class="mr-50"
      />
      Upload certificate
    </b-button>

    <b-modal
      v-model="show"
      title="Upload shipment certificate"
      centered
      no-close-on-backdrop
      :no-close-on-esc="loading"
      :hide-header-close="loading"
      @hidden="reset"
    >
      <b-form
        novalidate
        @submit.prevent="submit"
      >
        <b-form-group
          label="Certificate number"
          label-for="shipment-certificate-number"
        >
          <b-form-input
            id="shipment-certificate-number"
            v-model="form.certificate_number"
            trim
            placeholder="Enter the certificate number"
            :state="numberState"
            :disabled="loading"
          />
          <b-form-invalid-feedback>
            Certificate number is required
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="Document"
          label-for="shipment-document"
        >
          <b-form-file
            id="shipment-document"
            v-model="form.document"
            accept=".pdf,application/pdf"
            placeholder="Choose a PDF or drop it here…"
            drop-placeholder="Drop the file here…"
            :state="fileState"
            :disabled="loading"
          />
          <b-form-invalid-feedback>
            Please choose a document
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="loading"
          @click="show = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="loading"
          @click="submit"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="UploadCloudIcon"
            size="16"
            class="mr-50"
          />
          Upload
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'ShipmentUpload',
  props: {
    endpoint: {
      type: String,
      default: '/halal/shipments',
    },
  },
  data() {
    return {
      show: false,
      loading: false,
      submitted: false,
      form: {
        certificate_number: '',
        document: null,
      },
    }
  },
  computed: {
    numberState() {
      if (!this.submitted) return null
      return Boolean(this.form.certificate_number.trim())
    },
    fileState() {
      if (!this.submitted) return null
      return Boolean(this.form.document)
    },
  },
  methods: {
    open() {
      this.reset()
      this.show = true
    },
    reset() {
      this.submitted = false
      this.form = { certificate_number: '', document: null }
    },
    toast(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    },
    errorMessage(error, fallback) {
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') return msg
          if (msg && typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            if (firstList && firstList[0]) return firstList[0]
          }
        }
      } catch (e) {
        // keep fallback
      }
      return fallback
    },
    async submit() {
      this.submitted = true
      if (!this.numberState || !this.fileState || this.loading) return

      const formData = new FormData()
      formData.append('certificate_number', this.form.certificate_number.trim())
      formData.append('document', this.form.document)

      this.loading = true
      try {
        const response = await axios.post(this.endpoint, formData)
        if (response.status === 200 || response.status === 201) {
          this.toast('Certificate uploaded successfully')
          this.show = false
          this.$emit('uploaded')
        }
      } catch (error) {
        this.toast(this.errorMessage(error, 'Failed to upload the certificate'), 'danger', 'BellIcon')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
